import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingBag, Phone, User, Calendar, ExternalLink, CheckCircle, Clock, Truck, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const AdminMarketplaceOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPhotos, setSelectedPhotos] = useState(null);
    const [showPhotoModal, setShowPhotoModal] = useState(false);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/admin/marketplace/orders`);
            setOrders(response.data);
        } catch (error) {
            console.error("Error fetching orders:", error);
            toast.error("Pa kapab chaje kòmand Marketplace yo.");
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-HT', {
            style: 'decimal',
            minimumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('fr-HT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        if (status === 'completed') {
            return (
                <span className="px-4 py-1.5 rounded-xl bg-green-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-green-100">
                    <CheckCircle className="w-3 h-3" /> Peye
                </span>
            );
        }
        return (
            <span className="px-4 py-1.5 rounded-xl bg-amber-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-amber-100">
                <Clock className="w-3 h-3" /> Atant
            </span>
        );
    };

    const handleDeletePhoto = async (orderId, type) => {
        if (!window.confirm("Ou asire w ou vle efase foto sa a nèt nan baz done a? L'ap libere espas.")) return;
        try {
            await axios.delete(`${API}/admin/orders/${orderId}/photos/${type}`);
            toast.success("Foto a efase avek sikse!");
            setShowPhotoModal(false);
            fetchOrders();
        } catch (e) {
            toast.error("Erè nan efase foto a.");
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-4xl font-black text-[#232F3E] tracking-tighter italic uppercase leading-none">
                            Vant <span className="text-[#FF9900]">Marketplace</span>
                        </h1>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">
                            Tout kòmand kliyan yo pase dirèkteman nan men machann nou yo.
                        </p>
                    </div>
                    <div className="bg-white px-8 py-5 rounded-[25px] flex items-center gap-6 shadow-2xl shadow-gray-100 border border-gray-50 group hover:scale-105 transition-transform">
                        <div className="w-12 h-12 bg-[#FF9900]/10 rounded-2xl flex items-center justify-center text-[#FF9900]">
                            <ShoppingBag className="w-6 h-6" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Kòmand Reyisi</p>
                            <p className="text-3xl font-black text-gray-900 tracking-tighter">{orders.length}</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-32">
                        <div className="w-14 h-14 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <Card className="border-0 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.12)] rounded-[45px] overflow-hidden bg-white">
                        <CardContent className="p-0">
                            {orders.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gray-50/50 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100/50">
                                                <th className="py-8 px-10 text-left">Pwodwi / Tranzaksyon</th>
                                                <th className="py-8 px-8 text-left">Kliyan</th>
                                                <th className="py-8 px-8 text-left">Ekosistèm Peman</th>
                                                <th className="py-8 px-8 text-left">Distribisyon</th>
                                                <th className="py-8 px-8 text-left">Livrezon / Chofè</th>
                                                <th className="py-8 px-8 text-left">Estati</th>
                                                <th className="py-8 px-8 text-right pr-12">Dat</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {orders.map((order) => (
                                                <tr key={order.id} className="hover:bg-gray-50/80 transition-all border-b border-gray-50 last:border-0 group">
                                                    <td className="py-8 px-10">
                                                        <div className="flex flex-col">
                                                            <span className="font-black text-gray-900 text-lg tracking-tight group-hover:text-[#FF9900] transition-colors line-clamp-1">{order.product_title}</span>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <span className="text-[9px] text-gray-300 font-bold uppercase tracking-widest">ORDER: {order.id.slice(0, 8)}</span>
                                                                <div className="h-1 w-1 bg-gray-200 rounded-full"></div>
                                                                <span className="text-[9px] text-[#FF9900] font-bold uppercase italic">TX: MonCash</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <div className="flex flex-col gap-1">
                                                            <div className="flex items-center gap-2 text-gray-800 font-black text-xs">
                                                                <Phone className="w-3.5 h-3.5 text-gray-300" /> {order.buyer_phone}
                                                            </div>
                                                            <span className={`text-[9px] font-bold uppercase ${order.payment_status === 'completed' ? 'text-green-500' : 'text-amber-500'}`}>
                                                                {order.payment_status === 'completed' ? 'Kliyan Konfime' : 'Ap Tann Peman'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8 font-black text-gray-900 text-base italic tracking-tighter">
                                                        <div className="flex flex-col">
                                                            <span className={order.payment_status === 'completed' ? 'text-green-500' : 'text-[#FF9900]'}>
                                                                {formatPrice(order.price_htg)} HTG
                                                            </span>
                                                            <span className="text-[9px] text-gray-300 not-italic uppercase tracking-widest mt-1">
                                                                {order.payment_status === 'completed' ? 'Total Peye' : 'Total Pou Peye'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <div className="space-y-1.5">
                                                            <div className="flex justify-between items-center gap-4">
                                                                <span className="text-[9px] font-black text-gray-400 uppercase">Machann:</span>
                                                                <span className="text-[11px] font-black text-green-600">{formatPrice(order.seller_amount_htg || 0)} HTG</span>
                                                            </div>
                                                            <div className="flex justify-between items-center gap-4">
                                                                <span className="text-[9px] font-black text-gray-400 uppercase">Sistèm:</span>
                                                                <span className="text-[11px] font-black text-[#FF9900]">{formatPrice(order.commission_htg || 0)} HTG</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        {order.delivery_info?.driver_id ? (
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-100 shrink-0 bg-gray-50">
                                                                    {order.delivery_info.driver_photo ? (
                                                                        <img src={order.delivery_info.driver_photo} alt="Driver" className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center bg-[#232F3E] text-white text-[10px] font-black italic">
                                                                            {order.delivery_info.driver_name?.[0]}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[11px] font-black text-gray-900 leading-none mb-1">{order.delivery_info.driver_name}</span>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-[8px] font-black text-[#FF9900] uppercase tracking-tighter bg-[#FF9900]/5 px-1 rounded-sm">
                                                                            {order.delivery_info.status?.replace('_', ' ')}
                                                                        </span>
                                                                        <span className="text-[9px] font-bold text-gray-400">{order.delivery_info.driver_phone}</span>
                                                                    </div>
                                                                    
                                                                    {/* Add View Photos button if photos exist */}
                                                                    {(order.delivery_info.pickup_photo || order.delivery_info.delivery_photo) && (
                                                                        <button 
                                                                            onClick={() => {
                                                                                setSelectedPhotos({
                                                                                    orderId: order.id,
                                                                                    pickup: order.delivery_info.pickup_photo,
                                                                                    delivery: order.delivery_info.delivery_photo
                                                                                });
                                                                                setShowPhotoModal(true);
                                                                            }}
                                                                            className="mt-2 text-[9px] font-black uppercase tracking-widest text-[#FF9900] bg-[#FF9900]/10 hover:bg-[#FF9900]/20 px-2 py-1 rounded-lg w-fit transition-all flex items-center gap-1"
                                                                        >
                                                                            📸 Wè Foto Livrezon
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 text-gray-300 italic">
                                                                <Truck className="w-4 h-4 opacity-30" />
                                                                <span className="text-[10px] font-black uppercase tracking-widest leading-none">Pa gen chofè</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-8 px-8">
                                                        <div className="flex flex-col gap-2 items-start">
                                                            {getStatusBadge(order.payment_status)}
                                                            {order.payment_status !== 'completed' && (
                                                                <Button 
                                                                    size="sm" 
                                                                    onClick={async () => {
                                                                        if(!window.confirm("Eske ou asire ou te resevwa lajan sa manyèlman (pa egzanp MonCash transfè)? Sa ap voye kòb la nan bous machann nan.")) return;
                                                                        try {
                                                                            await axios.post(`${API}/marketplace/admin/orders/${order.id}/force-paid`);
                                                                            toast.success("Ou fòse kòmand sa kòm peye avèk siksè!");
                                                                            fetchOrders();
                                                                        } catch (e) {
                                                                            toast.error("Erè nan fòse peman an.");
                                                                        }
                                                                    }}
                                                                    className="h-6 px-2 text-[8px] font-black uppercase tracking-widest bg-[#232F3E] text-white hover:bg-[#FF9900] hover:text-black transition-colors rounded-md"
                                                                >
                                                                    Konfime Manyèlman
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-8 px-8 text-right pr-12">
                                                         <div className="flex flex-col items-end">
                                                            <span className="text-[11px] font-black text-gray-400 uppercase tracking-tighter italic">
                                                                {formatDate(order.created_at)}
                                                            </span>
                                                            <button className="mt-2 text-[9px] font-black text-[#FF9900] uppercase hover:underline flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                Detay <ExternalLink className="w-2.5 h-2.5" />
                                                            </button>
                                                         </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-48 text-gray-400 flex flex-col items-center">
                                    <ShoppingBag className="w-24 h-24 mb-8 opacity-5 italic" />
                                    <p className="font-black uppercase tracking-widest text-[11px]">Pa gen okenn kòmand Marketplace ankò.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* PHOTOS MODAL */}
            <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
                <DialogContent className="sm:max-w-[700px] bg-white rounded-[30px] p-0 overflow-hidden border-none shadow-2xl">
                    <DialogHeader className="p-8 bg-[#232F3E] text-white">
                        <DialogTitle className="text-2xl font-black italic tracking-tighter uppercase">Foto <span className="text-[#FF9900]">Livrezon</span></DialogTitle>
                        <DialogDescription className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-1">Prèv pickup magazen ak livrezon kliyan</DialogDescription>
                    </DialogHeader>
                    
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8 max-h-[70vh] overflow-y-auto">
                        {selectedPhotos?.pickup && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight flex items-center gap-2">
                                    <span className="w-6 h-6 bg-[#FF9900]/20 text-[#FF9900] rounded-full flex items-center justify-center">1</span>
                                    Foto Pickup (Magazen)
                                </h3>
                                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-inner bg-gray-50 h-[300px]">
                                    <img src={selectedPhotos.pickup} alt="Pickup" className="w-full h-full object-contain" />
                                </div>
                                <Button 
                                    onClick={() => handleDeletePhoto(selectedPhotos.orderId, 'pickup')}
                                    className="w-full bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 font-black uppercase text-[10px] h-10 rounded-xl"
                                >
                                    Efase Foto Pickup (Libere Espas)
                                </Button>
                            </div>
                        )}
                        
                        {selectedPhotos?.delivery && (
                            <div className="space-y-4">
                                <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight flex items-center gap-2">
                                    <span className="w-6 h-6 bg-blue-500/20 text-blue-500 rounded-full flex items-center justify-center">2</span>
                                    Foto Livrezon (Kliyan)
                                </h3>
                                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-inner bg-gray-50 h-[300px]">
                                    <img src={selectedPhotos.delivery} alt="Delivery" className="w-full h-full object-contain" />
                                </div>
                                <Button 
                                    onClick={() => handleDeletePhoto(selectedPhotos.orderId, 'delivery')}
                                    className="w-full bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 font-black uppercase text-[10px] h-10 rounded-xl"
                                >
                                    Efase Foto Livrezon (Libere Espas)
                                </Button>
                            </div>
                        )}
                        
                        {!selectedPhotos?.pickup && !selectedPhotos?.delivery && (
                            <div className="col-span-full py-12 text-center text-gray-400 font-black uppercase tracking-widest text-xs">
                                Pa gen okenn foto ki rive ankò.
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};
